import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    signal,
} from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";
import { PublicationModel } from "../../../../interfaces/publication-model";
import { PublicationCloneService } from "../../../../services/publication-clone.service";
import { PublicationService } from "../../../../services/publication.service";
import {
    AbstractControl,
    FormControl,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";

export interface CloneDialogData {
    publication: PublicationModel;
    existingNames: string[];
}

@Component({
    standalone: true,
    selector: "gwo-clone-publication-dialog",
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: "./clone-publication-dialog.component.html",
    styleUrl: "./clone-publication-dialog.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClonePublicationDialogComponent implements OnDestroy {
    private readonly dialogRef = inject(
        MatDialogRef<ClonePublicationDialogComponent>,
    );
    private readonly data = inject<CloneDialogData>(MAT_DIALOG_DATA);
    private readonly cloneService = inject(PublicationCloneService);
    private readonly publicationService = inject(PublicationService);
    private readonly destroy$ = new Subject<void>();

    readonly isSaving = signal(false);
    readonly saveError = signal<string | null>(null);

    readonly nameControl = new FormControl(
        generateUniqueName(
            this.data.publication.name,
            this.data.existingNames ?? [],
        ),
        [
            Validators.required,
            Validators.minLength(3),
            uniqueNameValidator(this.data.existingNames),
        ],
    );

    constructor() {
        this.nameControl.markAsTouched();
    }

    onSave(): void {
        if (this.nameControl.invalid || this.isSaving()) return;
        const newName = this.nameControl.value!.trim();
        if (!newName) {
            this.nameControl.setErrors({ required: true });
            return;
        }

        this.isSaving.set(true);
        this.saveError.set(null);
        this.nameControl.disable();

        const cloned = this.cloneService.clone(this.data.publication, newName);

        this.publicationService
            .savePublication(cloned)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (saved) => {
                    this.dialogRef.close(saved);
                },
                error: (err) => {
                    this.isSaving.set(false);
                    this.nameControl.enable();
                    this.saveError.set(
                        err?.error?.message === "NAME_TAKEN"
                            ? "Publikacja o tej nazwie już istnieje."
                            : "Wystąpił błąd podczas zapisu. Spróbuj ponownie.",
                    );
                },
            });
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

function uniqueNameValidator(existingNames: string[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const names = existingNames ?? [];
        const value = control.value?.trim().toLowerCase();
        if (!value) return null;
        const isTaken = names.some((name) => name.toLowerCase() === value);
        return isTaken ? { nameTaken: true } : null;
    };
}

function generateUniqueName(baseName: string, existingNames: string[]): string {
    const names = existingNames ?? [];
    const normalizedNames = names.map((n) => n.toLowerCase());
    const candidate = `Kopia - ${baseName}`;

    if (!normalizedNames.includes(candidate.toLowerCase())) {
        return candidate;
    }

    let counter = 2;
    while (
        normalizedNames.includes(`Kopia ${counter} - ${baseName}`.toLowerCase())
    ) {
        counter++;
    }
    return `Kopia ${counter} - ${baseName}`;
}
